/**
 * SB5-B.2 — Contract Positions list column definitions.
 *
 * Order locked per `03-page-contract-position.md` §Table:
 *   1) Position Code  (BIGINT, right-aligned, default sort DESC)
 *   2) Project Name   (string, sortable)
 *   3) Site Name      (string, sortable)
 *   4) Asset Name     (BE-emitted `assetDisplayLabel`, sortable)
 *   5) Status         (6-state chip)
 *   6) Edit           (pencil + "edit"; opens the Edit modal)
 */
import { Box, Button, Typography } from "@mui/material";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { ownershipColumn } from "@aeriosiq/ownership";
import { ContractPositionStatusChip } from "./ContractPositionStatusChip.js";
import type { ContractPositionListItem } from "./types.js";
import { PencilIcon } from "@aeriosiq/ui/icons";

export function buildContractPositionColumns(args: {
  onEdit: (row: ContractPositionListItem) => void;
  /** TNCY-9 — prepend the cross-tenant ownership tell only when the merged
   *  list actually has member rows, so an own-only tenant's list is
   *  byte-for-byte the pre-TNCY-9 table. */
  showOwnership?: boolean;
}): GridColDef<ContractPositionListItem>[] {
  const { onEdit, showOwnership = false } = args;
  return [
    ...(showOwnership
      ? [ownershipColumn<ContractPositionListItem>({ entityNoun: "position" })]
      : []),
    {
      field: "positionCode",
      headerName: "Position Code",
      sortable: true,
      flex: 0.6,
      minWidth: 120,
      // SB6-CONS-02 — identifier column is left-aligned. Christian
      // [00:37:53]: "Position code, again it should be aligned to the
      // left, now it's wrong."
      align: "left",
      headerAlign: "left",
      renderCell: (
        p: GridRenderCellParams<ContractPositionListItem, number>,
      ) => (
        <Box
          sx={{
            fontVariantNumeric: "tabular-nums",
            width: "100%",
            textAlign: "left",
          }}
        >
          {p.row.positionCode}
        </Box>
      ),
    },
    {
      field: "projectName",
      headerName: "Project Name",
      sortable: true,
      flex: 1.1,
      minWidth: 160,
      renderCell: (p) => (
        <Typography variant="body2" component="span">
          {p.row.projectName}
        </Typography>
      ),
    },
    {
      field: "siteName",
      headerName: "Site Name",
      sortable: true,
      flex: 1.0,
      minWidth: 140,
      valueGetter: (_v, row: ContractPositionListItem) => row.siteName ?? "",
      renderCell: (p) => (
        <Typography variant="body2" component="span">
          {p.row.siteName ?? "—"}
        </Typography>
      ),
    },
    {
      field: "assetDisplayLabel",
      headerName: "Asset Name",
      sortable: true,
      flex: 1.0,
      minWidth: 160,
      renderCell: (p) => (
        <Typography variant="body2" component="span">
          {p.row.assetDisplayLabel}
        </Typography>
      ),
    },
    {
      field: "state",
      headerName: "Status",
      sortable: true,
      flex: 0.7,
      minWidth: 130,
      renderCell: (p) => <ContractPositionStatusChip state={p.row.state} />,
    },
    {
      field: "__edit",
      headerName: "",
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      width: 96,
      align: "left",
      headerAlign: "left",
      renderCell: (p) => (
        <Button
          size="small"
          variant="text"
          startIcon={<PencilIcon fontSize="small" />}
          aria-label={`Edit contract position ${String(p.row.positionCode)}`}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(p.row);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];
}
